/**
 * Booking Confirmation Page
 *
 * Success screen shown after completing a booking.
 * Displays booking details, confirmation number, and next steps.
 *
 * SCRUM-710: API-fetch refactor — fetches booking details from API
 * instead of reading from URL params. Handles both single-booking
 * and cart (multi-date) confirmation flows.
 *
 * PG6 fix: Reads bookingId/cartId after 3DS redirect.
 * PG7 fix: confirmationNumber from API, never from URL params.
 *
 * Location: src/pages/BookingConfirmationPage.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 * Story: SCRUM-710 — Post-Booking Confirmation — All Dates
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Alert from '../../components/common/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import * as api from '../../utils/apiClient';
import { useCart } from '../../contexts/CartContext';
import { generateGoogleCalendarLink, generateICS, downloadICS } from '../../utils/icsGenerator';

/**
 * BookingConfirmationPage Component
 * Shows booking confirmation with details and next steps
 */
export default function BookingConfirmationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cart = useCart();
  const cartCleared = useRef(false);

  // State for API-fetched data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmationData, setConfirmationData] = useState(null);

  // URL params — priority: cartId > bookingId > confirmation (backward compat)
  const cartId = searchParams.get('cartId');
  const bookingId = searchParams.get('bookingId');
  const confirmationParam = searchParams.get('confirmation');

  // Fallback URL params for backward compatibility (placeholder flow)
  const urlTitle = searchParams.get('title') || 'Your Experience';
  const urlGuide = searchParams.get('guide') || 'Your Guide';
  const urlDate = searchParams.get('date');
  const urlGuests = searchParams.get('guests') || '1';
  const urlDuration = searchParams.get('duration') || '1 day';
  const urlEmail = searchParams.get('email');

  // SCRUM-710 AC-07: Clear cart on mount (once only)
  useEffect(() => {
    if (cartCleared.current) { return; }
    cartCleared.current = true;
    cart.clearCart();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch confirmation data from API
  useEffect(() => {
    let cancelled = false;

    async function fetchConfirmation() {
      try {
        if (cartId) {
          // Cart flow — fetch all cart bookings
          const response = await api.get(`cart/${cartId}/confirmation`);
          if (cancelled) { return; }

          if (!response.success) {
            throw new Error(response.error?.message || 'Failed to load cart confirmation');
          }

          setConfirmationData({
            type: 'cart',
            cartId,
            experienceTitle: response.data.experienceTitle,
            guideName: response.data.guideName,
            totalAmountCents: response.data.totalAmountCents,
            platformFeeCents: response.data.platformFeeCents,
            bookings: response.data.bookings,
            guests: urlGuests,
            email: urlEmail,
          });
        } else if (bookingId) {
          // Single booking — fetch booking detail
          const response = await api.get(`bookings/${bookingId}/detail`);
          if (cancelled) { return; }

          if (!response.success) {
            throw new Error(response.error?.message || 'Failed to load booking confirmation');
          }

          const booking = response.data;
          setConfirmationData({
            type: 'single',
            confirmationNumber: booking.booking_number,
            experienceTitle: booking.experience_title || urlTitle,
            guideName: booking.guide_first_name
              ? `${booking.guide_first_name} ${booking.guide_last_name || ''}`.trim()
              : urlGuide,
            startDate: booking.start_date,
            guests: booking.participant_count || urlGuests,
            email: booking.customer_email || urlEmail,
            totalAmountCents: booking.total_price_cents,
          });
        } else if (confirmationParam) {
          // Backward compat — placeholder flow uses URL params
          setConfirmationData({
            type: 'placeholder',
            confirmationNumber: confirmationParam,
            experienceTitle: urlTitle,
            guideName: urlGuide,
            startDate: urlDate,
            guests: urlGuests,
            duration: urlDuration,
            email: urlEmail,
          });
        } else {
          // No booking context — redirect to home
          navigate('/');
          return;
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load confirmation');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchConfirmation();

    return () => { cancelled = true; };
  }, [cartId, bookingId, confirmationParam, navigate, urlTitle, urlGuide, urlDate, urlGuests, urlDuration, urlEmail]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) { return ''; }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatPrice = (cents) => {
    if (!cents) { return '$0.00'; }
    return `$${(cents / 100).toFixed(2)}`;
  };

  // SCRUM-1199: Build trip object for calendar export from booking data
  const buildCalendarTrip = useCallback((booking, title, guide) => {
    const [firstName, ...lastParts] = (guide || 'Guide').split(' ');
    return {
      id: booking.bookingId || booking.confirmationNumber || `booking-${Date.now()}`,
      experience_title: title || 'Outdoor Adventure',
      start_date: booking.startDate || booking.start_date,
      duration_hours: 4,
      guide_first_name: firstName,
      guide_last_name: lastParts.join(' '),
      meeting_location_name: '',
    };
  }, []);

  const handleDownloadIcs = useCallback((booking, title, guide) => {
    const trip = buildCalendarTrip(booking, title, guide);
    const icsContent = generateICS(trip);
    const filename = `booking-${(trip.id || '').toString().slice(0, 8) || 'confirmation'}`;
    downloadICS(icsContent, filename);
  }, [buildCalendarTrip]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <LoadingSpinner message="Loading confirmation..." color="border-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-6">
          <Alert variant="error" message={error} className="mb-4" />
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!confirmationData) {
    return null;
  }

  const isCart = confirmationData.type === 'cart';
  const displayGuide = confirmationData.guideName;
  const displayTitle = confirmationData.experienceTitle;
  const displayEmail = confirmationData.email;
  const displayGuests = confirmationData.guests;

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="p-6 space-y-6 py-4">
          {/* Success Icon and Header */}
          <div className="text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <span className="text-4xl" role="img" aria-label="celebration">🎉</span>
            </div>
            <h2 className="text-2xl font-bold text-emerald-700 mt-4">Your Adventure is Booked!</h2>
            <p className="text-gray-600 mt-2">Get ready for an unforgettable experience</p>
          </div>

          {/* Booking Details */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-14 h-14 bg-gray-300 rounded-full flex items-center justify-center">
                <span className="text-2xl">👤</span>
              </div>
              <div>
                <p className="font-semibold">{displayTitle}</p>
                <p className="text-sm text-gray-600">with {displayGuide}</p>
              </div>
            </div>

            {isCart ? (
              /* Cart: show all booked dates with booking references */
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">
                  {displayGuests} {parseInt(displayGuests) === 1 ? 'guest' : 'guests'}
                </p>

                <div className="border-t pt-3">
                  <p className="text-xs text-gray-500 mb-2">Booked Dates</p>
                  {confirmationData.bookings.map((booking) => (
                    <div key={booking.bookingId} className="py-2 border-b border-gray-100 last:border-0">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-gray-700">{formatDate(booking.startDate)}</p>
                          <p className="text-xs text-gray-500 font-mono">Ref: {booking.bookingNumber}</p>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{formatPrice(booking.priceCents)}</span>
                      </div>
                      <div className="flex gap-3 mt-1" data-testid={`calendar-links-${booking.bookingId}`}>
                        <a
                          href={generateGoogleCalendarLink(buildCalendarTrip(booking, displayTitle, displayGuide))}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-emerald-600 hover:underline"
                        >
                          Add to Google Calendar
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDownloadIcs(booking, displayTitle, displayGuide)}
                          className="text-xs text-emerald-600 hover:underline"
                        >
                          Download .ics
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <p className="font-semibold text-gray-900">Total</p>
                    <p className="font-semibold text-lg text-gray-900">{formatPrice(confirmationData.totalAmountCents)}</p>
                  </div>
                </div>
              </div>
            ) : (
              /* Single booking: show date and confirmation number */
              <>
                <div className="text-sm space-y-1">
                  <p>{formatDate(confirmationData.startDate)}</p>
                  <p>{displayGuests} {parseInt(displayGuests) === 1 ? 'guest' : 'guests'}{confirmationData.duration ? ` • ${confirmationData.duration}` : ''}</p>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-gray-500">Confirmation #</p>
                  <p className="font-mono font-semibold text-lg" data-testid="confirmation-number">{confirmationData.confirmationNumber}</p>
                </div>
                {confirmationData.startDate && (
                  <div className="flex gap-3 mt-2" data-testid="calendar-links-single">
                    <a
                      href={generateGoogleCalendarLink(buildCalendarTrip(confirmationData, displayTitle, displayGuide))}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-600 hover:underline"
                    >
                      Add to Google Calendar
                    </a>
                    <button
                      type="button"
                      onClick={() => handleDownloadIcs(confirmationData, displayTitle, displayGuide)}
                      className="text-xs text-emerald-600 hover:underline"
                    >
                      Download .ics
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* What's Next */}
          <Alert variant="info" className="rounded-xl">
            <div>
              <p className="font-semibold text-blue-900 mb-2">What&apos;s Next?</p>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>✓ Confirmation email sent to {displayEmail || 'your email'}</li>
                <li>✓ {displayGuide} will message you within 24 hours</li>
                <li>✓ Trip details sent 7 days before</li>
              </ul>
            </div>
          </Alert>

          {/* Action Buttons */}
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-white border-2 border-emerald-500 text-emerald-600 font-semibold rounded-lg hover:bg-emerald-50"
          >
            💬 Message {displayGuide}
          </button>

          <button
            onClick={() => navigate('/trips')}
            className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600"
          >
            View My Trip
          </button>
        </div>
      </div>
    </div>
  );
}
